import React from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import { DetailButton } from '../common/detail'
import '../common/detail/detail.css'
import '../common/form/form.css'
import NumberInput from '../common/NumberInput'

type FixWeightTargetScorermProps<T> = {
  values: T
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  onSubmit: () => void
  onCancel: () => void
  fields: Array<{
    type: string
    label: string
    name: keyof T
    required?: boolean
    fullWidth?: boolean
  }>
}

const FixWeightTargetScore = <T extends Record<string, any>>({
  values,
  onChange,
  onSubmit,
  onCancel,
  fields,
}: FixWeightTargetScorermProps<T>) => {
  const { t } = useTranslation()

  return (
    <form
      className="maturity-weight-form"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      <div className="incyia-modal-head">
        <div>
          <h3>{t('popups.fixWeightTargetScore.title')}</h3>
          <p>
            {t('popups.fixWeightTargetScore.subtitle', {
              defaultValue:
                'Set weight and target score so this subdomain can be used on the task.',
            })}
          </p>
        </div>
        <button
          type="button"
          className="incyia-modal-x"
          onClick={onCancel}
          aria-label={t('popups.fixWeightTargetScore.cancel')}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      <div className="incyia-modal-body">
        <div className="incyia-fgrid incyia-fgrid--one" style={{ maxWidth: 'none' }}>
          {fields.map((field, index) => {
            const raw = values[field.name]
            const display =
              typeof raw === 'number' && raw < 0 ? '' : (raw ?? '')

            return (
              <div key={index} className="incyia-ffield">
                <label className="incyia-ffield-label">
                  {field.label}
                  {field.required ? (
                    <span className="incyia-ffield-req">*</span>
                  ) : null}
                </label>
                <NumberInput
                  name={field.name as string}
                  value={display === '' ? null : (display as number)}
                  onChange={(v) =>
                    onChange({
                      target: { name: field.name as string, value: v ?? -1 },
                    } as any)
                  }
                  required={field.required}
                  min={0}
                  max={
                    field.name === 'Weight'
                      ? 4
                      : field.name === 'TargetScore'
                        ? 5
                        : undefined
                  }
                />
                <span className="incyia-ffield-help">
                  {field.name === 'Weight'
                    ? t('popups.fixWeightTargetScore.weightHelp', {
                        defaultValue: 'Value from 0 to 4.',
                      })
                    : t('popups.fixWeightTargetScore.targetHelp', {
                        defaultValue: 'Value from 0 to 5.',
                      })}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      <div className="incyia-modal-foot">
        <DetailButton type="button" variant="ghost" onClick={onCancel}>
          {t('popups.fixWeightTargetScore.cancel')}
        </DetailButton>
        <DetailButton type="submit">
          {t('popups.fixWeightTargetScore.update')}
        </DetailButton>
      </div>
    </form>
  )
}

export default FixWeightTargetScore
